import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../config/config';
import { 
  FaUserTie, FaTasks, FaClock, FaChartLine, FaSmile, 
  FaPlus, FaCheckCircle, FaExclamationCircle, FaProjectDiagram,
  FaTrash, FaEdit, FaCamera, FaFileUpload
} from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { usePermission } from '../../../hooks/usePermission';

const DEFAULT_AVATARS = [
  "https://api.dicebear.com/9.x/adventurer/svg?seed=Brian",
  "https://api.dicebear.com/9.x/adventurer/svg?seed=Brooklynn",
  "https://api.dicebear.com/9.x/adventurer/svg?seed=Aidan",
  "https://api.dicebear.com/9.x/adventurer/svg?seed=Destiny",
  "https://api.dicebear.com/9.x/adventurer/svg?seed=Katherine",
  "https://api.dicebear.com/9.x/adventurer/svg?seed=Vivian",
];

const TeamPerformance = () => {
  const { can } = usePermission();
  const [loading, setLoading] = useState(true);
  const [teamData, setTeamData] = useState([]);
  
  // Modals State
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showMetricsModal, setShowMetricsModal] = useState(false);

  // Edit/View State
  const [editingMember, setEditingMember] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedMemberForMetrics, setSelectedMemberForMetrics] = useState(null);

  // Forms Data
  const [memberForm, setMemberForm] = useState({ name: '', role: '', avatar_url: '', avatarFile: null });
  const [taskForm, setTaskForm] = useState({ title: '', project_name: '', deadline: '', member_id: null, status: 'Pending' });
  const [metricsForm, setMetricsForm] = useState({ 
    hours_worked: 0, productivity_score: 0, csat_wetekup: 0, csat_wecapture: 0, notes: '', 
    period_start: new Date().toISOString().split('T')[0], 
    period_end: new Date().toISOString().split('T')[0] 
  });

  // Track which members have completed tasks visible
  const [showCompletedTasks, setShowCompletedTasks] = useState({});

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      const response = await axios.get(`${SERVER}/team`, { withCredentials: true });
      console.log('Fetched team data:', response.data);
      setTeamData(response.data);
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  // --- Member Handlers ---

  const handleMemberSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('name', memberForm.name);
    formData.append('role', memberForm.role);
    if (memberForm.avatarFile) {
      formData.append('avatar', memberForm.avatarFile);
    } else if (memberForm.avatar_url) {
      formData.append('avatar_url', memberForm.avatar_url);
    }

    try {
      if (editingMember) {
        if (!can('team_performance.edit')) return alert("Permission denied");
        await axios.put(`${SERVER}/team/members/${editingMember.id}`, formData, { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        if (!can('team_performance.create')) return alert("Permission denied");
        await axios.post(`${SERVER}/team/members`, formData, { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      closeMemberModal();
      fetchTeamData();
    } catch (error) {
      console.error('Error saving member:', error);
    }
  };

  const handleDeleteMember = async (id) => {
    if (!can('team_performance.delete')) return alert("Permission denied");
    if (!window.confirm("Are you sure? This will delete the member and all associated tasks/metrics.")) return;
    try {
      await axios.delete(`${SERVER}/team/members/${id}`, { withCredentials: true });
      fetchTeamData();
    } catch (error) {
      console.error('Error deleting member:', error);
    }
  };

  const openAddMemberModal = () => {
    if (!can('team_performance.create')) return alert("Permission denied");
    setEditingMember(null);
    setMemberForm({ name: '', role: '', avatar_url: '', avatarFile: null });
    setShowMemberModal(true);
  };

  const openEditMemberModal = (member) => {
    if (!can('team_performance.edit')) return alert("Permission denied");
    setEditingMember(member);
    setMemberForm({ name: member.name, role: member.role, avatar_url: member.avatar_url, avatarFile: null });
    setShowMemberModal(true);
  };

  const closeMemberModal = () => {
    setShowMemberModal(false);
    setEditingMember(null);
  };

  // --- Task Handlers ---

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTask) {
        if (!can('tasks.edit')) return alert("Permission denied");
        await axios.put(`${SERVER}/team/tasks/${editingTask.id}`, taskForm, { withCredentials: true });
      } else {
        if (!can('tasks.create')) return alert("Permission denied");
        await axios.post(`${SERVER}/team/tasks`, taskForm, { withCredentials: true });
      }
      closeTaskModal();
      fetchTeamData();
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const handleDeleteTask = async (id) => {
    if (!can('tasks.delete')) return alert("Permission denied");
    if (!window.confirm("Delete this task?")) return;
    try {
      await axios.delete(`${SERVER}/team/tasks/${id}`, { withCredentials: true });
      fetchTeamData();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const openAssignTaskModal = (memberId) => {
    if (!can('tasks.create')) return alert("Permission denied");
    setEditingTask(null);
    setTaskForm({ title: '', project_name: '', deadline: '', member_id: memberId, status: 'Pending' });
    setShowTaskModal(true);
  };

  const openEditTaskModal = (task, memberId) => {
    if (!can('tasks.edit')) return alert("Permission denied");
    setEditingTask(task);
    setTaskForm({ 
      title: task.title, 
      project_name: task.project_name, 
      deadline: task.deadline ? task.deadline.split('T')[0] : '', 
      member_id: memberId,
      status: task.status 
    });
    setShowTaskModal(true);
  };

  const closeTaskModal = () => setShowTaskModal(false);

  // --- Metrics Handlers ---

  const handleMetricsSubmit = async (e) => {
    e.preventDefault();
    if (!can('team_performance.create')) return alert("Permission denied");
    try {
      console.log('Submitting metrics:', { ...metricsForm, member_id: selectedMemberForMetrics.id });
      const response = await axios.post(`${SERVER}/team/metrics`, { ...metricsForm, member_id: selectedMemberForMetrics.id }, { withCredentials: true });
      console.log('Metrics saved successfully:', response.data);
      setShowMetricsModal(false);
      // Small delay to ensure database consistency before refresh
      setTimeout(() => {
        fetchTeamData();
      }, 300);
    } catch (error) {
      console.error('Error saving metrics:', error);
      alert('Failed to save metrics. Please try again.');
    }
  };

  const openMetricsModal = (member) => {
    if (!can('team_performance.create')) return alert("Permission denied");
    setSelectedMemberForMetrics(member);
    // Always set current dates for the new period
    const today = new Date().toISOString().split('T')[0];
    
    // Pre-fill with latest metrics if exists, or use defaults
    if (member.latest_metrics) {
        setMetricsForm({
            hours_worked: member.latest_metrics.hours_worked || 0,
            productivity_score: member.latest_metrics.productivity_score || 0,
            csat_wetekup: member.latest_metrics.csat_wetekup || 0,
            csat_wecapture: member.latest_metrics.csat_wecapture || 0,
            notes: member.latest_metrics.notes || '',
            period_start: today,
            period_end: today
        });
    } else {
        setMetricsForm({ 
            hours_worked: 0, 
            productivity_score: 0, 
            csat_wetekup: 0, 
            csat_wecapture: 0, 
            notes: '', 
            period_start: today, 
            period_end: today 
        });
    }
    setShowMetricsModal(true);
  };


  // Helper Stats
  const calculateTeamStats = () => {
    if (!teamData.length) return { productivity: 0, csatWeTekup: 0, csatWeCapture: 0 };
    let totalProd = 0, totalWeTekup = 0, totalWeCapture = 0, count = 0;
    teamData.forEach(m => {
      if (m.latest_metrics) {
        totalProd += m.latest_metrics.productivity_score || 0;
        totalWeTekup += m.latest_metrics.csat_wetekup || 0;
        totalWeCapture += m.latest_metrics.csat_wecapture || 0;
        count++;
      }
    });
    return {
      productivity: count ? Math.round(totalProd / count) : 0,
      csatWeTekup: count ? Math.round(totalWeTekup / count) : 0,
      csatWeCapture: count ? Math.round(totalWeCapture / count) : 0
    };
  };
  const stats = calculateTeamStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-blue-600 absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 rounded-3xl shadow-2xl p-8 animate-scaleIn">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/20 rounded-full -ml-48 -mb-48 blur-3xl" />
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <FaUserTie className="text-3xl text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-1">Team Performance</h2>
              <p className="text-blue-100 text-lg">Manage your team, tasks, and track success</p>
            </div>
          </div>
          {can('team_performance.create') && (
            <button 
              onClick={openAddMemberModal} 
              className="px-6 py-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl border border-white/30 flex items-center gap-2 group"
            >
              <FaPlus className="group-hover:rotate-90 transition-transform duration-300" />
              <span>Add Member</span>
            </button>
          )}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="group relative bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-xl p-6 overflow-hidden animate-slideUp hover:scale-105 transition-transform duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="relative z-10 flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <FaClock className="text-2xl text-white" />
            </div>
            <div>
              <p className="text-purple-100 text-sm font-semibold uppercase tracking-wider">Avg Productivity</p>
              <h3 className="text-4xl font-bold text-white">{stats.productivity}%</h3>
            </div>
          </div>
        </div>
        
        <div className="group relative bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl shadow-xl p-6 overflow-hidden animate-slideUp hover:scale-105 transition-transform duration-300" style={{ animationDelay: '0.1s' }}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="relative z-10 flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <FaSmile className="text-2xl text-white" />
            </div>
            <div>
              <p className="text-blue-100 text-sm font-semibold uppercase tracking-wider">CSAT WeTekup</p>
              <h3 className="text-4xl font-bold text-white">{stats.csatWeTekup}<span className="text-xl">/100</span></h3>
            </div>
          </div>
        </div>
        
        <div className="group relative bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl shadow-xl p-6 overflow-hidden animate-slideUp hover:scale-105 transition-transform duration-300" style={{ animationDelay: '0.2s' }}>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="relative z-10 flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <FaSmile className="text-2xl text-white" />
            </div>
            <div>
              <p className="text-pink-100 text-sm font-semibold uppercase tracking-wider">CSAT WeCapture</p>
              <h3 className="text-4xl font-bold text-white">{stats.csatWeCapture}<span className="text-xl">/100</span></h3>
            </div>
          </div>
        </div>
      </div>

      {/* Members Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {teamData.map((member, index) => (
          <div key={member.id} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden flex flex-col relative group hover:shadow-2xl transition-all duration-500 animate-slideUp" style={{ animationDelay: `${index * 0.1}s` }}>
            {/* Member Actions */}
            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10">
                {can('team_performance.edit') && (
                  <button 
                    onClick={() => openEditMemberModal(member)} 
                    className="p-2.5 bg-white/90 backdrop-blur-sm hover:bg-blue-50 text-slate-600 hover:text-blue-600 rounded-xl shadow-lg hover:scale-110 transition-all duration-200"
                  >
                    <FaEdit />
                  </button>
                )}
                {can('team_performance.delete') && (
                  <button 
                    onClick={() => handleDeleteMember(member.id)} 
                    className="p-2.5 bg-white/90 backdrop-blur-sm hover:bg-red-50 text-slate-600 hover:text-red-600 rounded-xl shadow-lg hover:scale-110 transition-all duration-200"
                  >
                    <FaTrash />
                  </button>
                )}
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-slate-200/50 flex items-start gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center overflow-hidden border-4 border-white shadow-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  {member.avatar_url ? (
                    <img src={member.avatar_url.startsWith('http') ? member.avatar_url : `${SERVER}${member.avatar_url}`} alt={member.name} className="w-full h-full object-cover" />
                  ) : <FaUserTie className="text-white text-2xl" />}
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div className="flex-1 min-w-0 pr-16">
                <h3 className="text-xl font-bold text-slate-800 truncate">{member.name}</h3>
                <span className="inline-block px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs rounded-full font-bold mt-2 truncate max-w-full shadow-lg">
                  {member.role}
                </span>
                {can('team_performance.create') && (
                  <button 
                    onClick={() => openMetricsModal(member)} 
                    className="text-xs text-blue-600 hover:text-blue-800 font-bold mt-3 flex items-center gap-1.5 hover:gap-2 transition-all duration-200"
                  >
                    <FaChartLine /> Manage Metrics →
                  </button>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-6 bg-gradient-to-br from-slate-50 to-white space-y-5 flex-1">
              {member.latest_metrics ? (
                <>
                   <div className="flex justify-between items-center p-3 bg-white rounded-xl shadow-sm">
                     <span className="text-slate-600 font-semibold text-sm flex items-center gap-2">
                       <FaClock className="text-purple-500" /> Hours
                     </span>
                     <span className="font-bold text-slate-800 text-lg">{member.latest_metrics.hours_worked}h</span>
                   </div>
                   
                   <div className="p-3 bg-white rounded-xl shadow-sm">
                     <div className="flex justify-between items-center mb-2">
                       <span className="text-slate-600 font-semibold text-sm flex items-center gap-2">
                         <FaChartLine className="text-green-500" /> Productivity
                       </span>
                       <span className="font-bold text-slate-800">{member.latest_metrics.productivity_score}%</span>
                     </div>
                     <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                       <div 
                         className="h-full bg-gradient-to-r from-green-500 to-emerald-600 rounded-full transition-all duration-1000 ease-out" 
                         style={{ width: `${member.latest_metrics.productivity_score}%` }} 
                       />
                     </div>
                   </div>
                   
                   <div className="grid grid-cols-2 gap-3">
                     <div className="bg-gradient-to-br from-blue-500 to-cyan-600 p-4 rounded-xl text-center shadow-lg hover:scale-105 transition-transform duration-300">
                       <div className="text-xs text-blue-100 font-bold uppercase tracking-wider mb-1">WeTekup</div>
                       <div className="text-3xl font-bold text-white">{member.latest_metrics.csat_wetekup || '-'}</div>
                     </div>
                     <div className="bg-gradient-to-br from-pink-500 to-rose-600 p-4 rounded-xl text-center shadow-lg hover:scale-105 transition-transform duration-300">
                       <div className="text-xs text-pink-100 font-bold uppercase tracking-wider mb-1">WeCapture</div>
                       <div className="text-3xl font-bold text-white">{member.latest_metrics.csat_wecapture || '-'}</div>
                     </div>
                   </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FaChartLine className="text-slate-400 text-2xl" />
                  </div>
                  <p className="text-slate-400 text-sm font-medium">No metrics yet</p>
                </div>
              )}
            </div>

            {/* Tasks */}
            <div className="p-6 border-t border-slate-200/50 bg-slate-50/50">
               <div className="flex justify-between items-center mb-4">
                 <h4 className="font-bold text-slate-800 flex items-center gap-2">
                   <FaTasks className="text-blue-600" /> Tasks
                 </h4>
                 <div className="flex items-center gap-3">
                   {/* Toggle for completed tasks */}
                   <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-600 bg-white px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                     <input 
                       type="checkbox" 
                       checked={showCompletedTasks[member.id] || false}
                       onChange={(e) => setShowCompletedTasks({...showCompletedTasks, [member.id]: e.target.checked})}
                       className="w-4 h-4 rounded text-blue-600 focus:ring-2 focus:ring-blue-500"
                     />
                     <span className="select-none font-medium">Completed</span>
                   </label>
                   {can('tasks.create') && (
                     <button 
                       onClick={() => openAssignTaskModal(member.id)} 
                       className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-xs font-bold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-1"
                     >
                       <FaPlus /> Assign
                     </button>
                   )}
                 </div>
               </div>
               <div className="space-y-3 max-h-48 overflow-y-auto scrollbar-thin">
                 {(() => {
                   const filteredTasks = member.active_tasks?.filter(task => 
                     showCompletedTasks[member.id] || task.status !== 'Completed'
                   ) || [];
                   
                   return filteredTasks.length > 0 ? (
                     filteredTasks.map((task, taskIndex) => (
                       <div key={task.id} className="group/task bg-white border-2 border-slate-200 hover:border-blue-400 p-3 rounded-xl transition-all duration-300 flex justify-between items-start shadow-sm hover:shadow-md">
                          <div className="flex-1">
                              <p className="font-semibold text-slate-800 text-sm mb-2">{task.title}</p>
                              <div className="flex flex-wrap gap-2">
                                  <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                                    task.status === 'Completed' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' :
                                    task.status === 'In Progress' ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white' : 
                                    'bg-slate-200 text-slate-600'
                                  }`}>{task.status}</span>
                                  {task.deadline && (
                                    <span className="text-xs px-2.5 py-1 bg-red-100 text-red-700 rounded-full font-semibold flex items-center gap-1">
                                      <FaClock size={10} />
                                      {new Date(task.deadline).toLocaleDateString()}
                                    </span>
                                  )}
                              </div>
                          </div>
                          <div className="flex gap-1.5 opacity-0 group-hover/task:opacity-100 transition-opacity duration-200 ml-2">
                              {can('tasks.edit') && (
                                <button 
                                  onClick={() => openEditTaskModal(task, member.id)} 
                                  className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all hover:scale-110"
                                >
                                  <FaEdit size={12} />
                                </button>
                              )}
                              {can('tasks.delete') && (
                                <button 
                                  onClick={() => handleDeleteTask(task.id)} 
                                  className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all hover:scale-110"
                                >
                                  <FaTrash size={12} />
                                </button>
                              )}
                          </div>
                       </div>
                     ))
                   ) : (
                     <div className="text-center py-6">
                       <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2">
                         <FaTasks className="text-slate-400" />
                       </div>
                       <p className="text-xs text-slate-400 font-medium">No {showCompletedTasks[member.id] ? '' : 'active '}tasks</p>
                     </div>
                   );
                 })()}
               </div>
            </div>
          </div>
        ))}
        {/* Add Button Card */}
        {can('team_performance.create') && (
          <button 
            onClick={openAddMemberModal} 
            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border-2 border-dashed border-slate-300 hover:border-blue-500 flex flex-col items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-gradient-to-br hover:from-blue-50 hover:to-indigo-50 transition-all duration-500 min-h-[400px] group animate-slideUp"
            style={{ animationDelay: `${teamData.length * 0.1}s` }}
          >
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 group-hover:from-blue-500 group-hover:to-indigo-600 flex items-center justify-center mb-4 transition-all duration-300 shadow-lg group-hover:scale-110">
              <FaPlus className="text-3xl group-hover:text-white group-hover:rotate-90 transition-all duration-300" />
            </div>
            <span className="font-bold text-lg">Add Team Member</span>
            <span className="text-sm mt-1">Click to create new member</span>
          </button>
        )}
      </div>

      {/* --- MODALS --- */}

      {/* Member Modal (Add/Edit) */}
      {showMemberModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fadeIn overflow-y-auto" style={{ position: 'fixed', top: -40, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-3xl w-full max-w-md animate-scaleIn overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <FaUserTie className="text-2xl text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">{editingMember ? 'Edit Member' : 'Add New Member'}</h3>
                </div>
                <button 
                  onClick={closeMemberModal}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-200 group"
                >
                  <MdClose className="text-xl text-white group-hover:rotate-90 transition-transform duration-300" />
                </button>
            </div>
            <form onSubmit={handleMemberSubmit} className="p-8 space-y-6">
                <div>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                      <span>👤</span>
                      Name
                    </label>
                    <input 
                      type="text" 
                      required 
                      value={memberForm.name} 
                      onChange={e => setMemberForm({...memberForm, name: e.target.value})} 
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                      placeholder="Enter member name" 
                    />
                </div>
                <div>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                      <span>💼</span>
                      Role
                    </label>
                    <input 
                      type="text" 
                      required 
                      value={memberForm.role} 
                      onChange={e => setMemberForm({...memberForm, role: e.target.value})} 
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                      placeholder="e.g., Developer, Designer" 
                    />
                </div>
                
                {/* Avatar Selection */}
                <div>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3">
                      <span>📸</span>
                      Avatar
                    </label>
                    <div className="space-y-4">
                        {/* File Upload */}
                        <div className="flex items-center gap-3">
                            <label className="cursor-pointer bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 px-4 py-3 rounded-xl text-sm text-blue-700 font-semibold flex items-center gap-2 transition-all duration-200 border-2 border-blue-200">
                                <FaFileUpload /> Upload Photo
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                                    if(e.target.files[0]) setMemberForm({...memberForm, avatarFile: e.target.files[0], avatar_url: ''});
                                }} />
                            </label>
                            {memberForm.avatarFile && (
                              <span className="text-xs text-green-600 font-semibold truncate bg-green-50 px-3 py-1.5 rounded-lg">
                                ✓ {memberForm.avatarFile.name}
                              </span>
                            )}
                        </div>
                        
                        {/* Default Avatars */}
                        <div>
                            <p className="text-xs text-slate-600 font-semibold mb-3">Or choose a default avatar:</p>
                            <div className="flex gap-3 flex-wrap">
                                {DEFAULT_AVATARS.map((url, i) => (
                                    <button 
                                      type="button" 
                                      key={i} 
                                      onClick={() => setMemberForm({...memberForm, avatar_url: url, avatarFile: null})} 
                                      className={`w-14 h-14 rounded-xl border-3 overflow-hidden transition-all duration-200 ${memberForm.avatar_url === url ? 'border-blue-500 scale-110 shadow-lg' : 'border-slate-200 hover:border-blue-300 hover:scale-105'}`}
                                    >
                                        <img src={url} alt="avatar" className="w-full h-full" />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* URL Input */}
                        <input 
                          type="text" 
                          value={memberForm.avatar_url} 
                          onChange={e => setMemberForm({...memberForm, avatar_url: e.target.value, avatarFile: null})} 
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                          placeholder="Or paste image URL..." 
                        />
                    </div>
                </div>

                <div className="flex gap-4 pt-6 border-t-2 border-slate-200">
                    <button 
                      type="button" 
                      onClick={closeMemberModal} 
                      className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      Save Member
                    </button>
                </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Modal (Add/Edit) */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fadeIn overflow-y-auto" style={{ position: 'fixed', top: -40, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-3xl w-full max-w-md animate-scaleIn overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-6 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <FaTasks className="text-2xl text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">{editingTask ? 'Edit Task' : 'Assign Task'}</h3>
                </div>
                <button 
                  onClick={closeTaskModal}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-200 group"
                >
                  <MdClose className="text-xl text-white group-hover:rotate-90 transition-transform duration-300" />
                </button>
            </div>
            <form onSubmit={handleTaskSubmit} className="p-8 space-y-6">
                <div>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                      <span>📝</span>
                      Task Title
                    </label>
                    <input 
                      type="text" 
                      required 
                      value={taskForm.title} 
                      onChange={e => setTaskForm({...taskForm, title: e.target.value})} 
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                      placeholder="Enter task title"
                    />
                </div>
                <div>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                      <span>📁</span>
                      Project Name
                    </label>
                    <input 
                      type="text" 
                      value={taskForm.project_name} 
                      onChange={e => setTaskForm({...taskForm, project_name: e.target.value})} 
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                      placeholder="General" 
                    />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                          <span>📅</span>
                          Deadline
                        </label>
                        <input 
                          type="date" 
                          required 
                          value={taskForm.deadline} 
                          onChange={e => setTaskForm({...taskForm, deadline: e.target.value})} 
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                        />
                    </div>
                    <div>
                        <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                          <span>🎯</span>
                          Status
                        </label>
                        <select 
                          value={taskForm.status} 
                          onChange={e => setTaskForm({...taskForm, status: e.target.value})} 
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                        >
                            <option value="Pending">Pending</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                        </select>
                    </div>
                </div>
                <div className="flex gap-4 pt-6 border-t-2 border-slate-200">
                    <button 
                      type="button" 
                      onClick={closeTaskModal} 
                      className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      Save Task
                    </button>
                </div>
            </form>
          </div>
        </div>
      )}

      {/* Metrics Modal */}
      {showMetricsModal && selectedMemberForMetrics && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-fadeIn overflow-y-auto" style={{ position: 'fixed', top: -40, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-3xl w-full max-w-lg animate-scaleIn overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <FaChartLine className="text-2xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">Update Metrics</h3>
                    <p className="text-green-100 text-sm">{selectedMemberForMetrics.name}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowMetricsModal(false)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-200 group"
                >
                  <MdClose className="text-xl text-white group-hover:rotate-90 transition-transform duration-300" />
                </button>
            </div>
            <form onSubmit={handleMetricsSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto scrollbar-thin">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="flex items-center gap-2 text-xs font-bold text-slate-700 uppercase mb-2">
                          <FaClock className="text-purple-600" />
                          Hours Worked
                        </label>
                        <input 
                          type="number" 
                          step="0.5" 
                          value={metricsForm.hours_worked} 
                          onChange={e => setMetricsForm({...metricsForm, hours_worked: e.target.value})} 
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" 
                        />
                    </div>
                    <div>
                        <label className="flex items-center gap-2 text-xs font-bold text-slate-700 uppercase mb-2">
                          <FaChartLine className="text-green-600" />
                          Productivity %
                        </label>
                        <input 
                          type="number" 
                          min="0" 
                          max="100" 
                          value={metricsForm.productivity_score} 
                          onChange={e => setMetricsForm({...metricsForm, productivity_score: e.target.value})} 
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" 
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="flex items-center gap-2 text-xs font-bold text-blue-600 uppercase mb-2">
                          <FaSmile />
                          CSAT (WeTekup)
                        </label>
                        <input 
                          type="number" 
                          min="0" 
                          max="100" 
                          value={metricsForm.csat_wetekup} 
                          onChange={e => setMetricsForm({...metricsForm, csat_wetekup: e.target.value})} 
                          className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                        />
                    </div>
                    <div>
                        <label className="flex items-center gap-2 text-xs font-bold text-pink-600 uppercase mb-2">
                          <FaSmile />
                          CSAT (WeCapture)
                        </label>
                        <input 
                          type="number" 
                          min="0" 
                          max="100" 
                          value={metricsForm.csat_wecapture} 
                          onChange={e => setMetricsForm({...metricsForm, csat_wecapture: e.target.value})} 
                          className="w-full px-4 py-3 border-2 border-pink-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all" 
                        />
                    </div>
                </div>
                <div>
                    <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2">
                      <span>📝</span>
                      Notes / Performance Review
                    </label>
                    <textarea 
                      value={metricsForm.notes} 
                      onChange={e => setMetricsForm({...metricsForm, notes: e.target.value})} 
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32 resize-none transition-all" 
                      placeholder="Enter periodic review notes..."
                    ></textarea>
                </div>
                <div className="flex gap-4 pt-6 border-t-2 border-slate-200">
                    <button 
                      type="button" 
                      onClick={() => setShowMetricsModal(false)} 
                      className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="flex-1 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      Update Metrics
                    </button>
                </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPerformance;
